import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatTemplate from '../templates/ChatTemplate';
import SEO from '../atoms/SEO';
import FloatingModelsContainer from '../organisms/FloatingModelsContainer';
import { useChat } from '../../context/ChatContext';
import './ChatPage.css';

const ChatPage = () => {
  const navigate = useNavigate();
  const chatPageRef = useRef(null);
  const { 
    startConversation, 
    conversationStarted, 
    hasPdfContent, 
    mode, 
    selectedChapter 
  } = useChat();

  // Redirect to landing page if no PDF has been uploaded and not in tutor mode with a chapter
  useEffect(() => {
    if (!hasPdfContent) {
      navigate('/');
    }
  }, [hasPdfContent, navigate]);

  // Start the conversation if it hasn't started yet
  useEffect(() => {
    if (!conversationStarted && hasPdfContent) {
      startConversation();
    }
  }, [startConversation, conversationStarted, hasPdfContent]);

  // Make sure the chat is fully visible within the viewport
  useEffect(() => {
    // Adjust positioning if needed
    const adjustChatPosition = () => {
      if (!chatPageRef.current) return;
      
      // Get window dimensions
      const windowHeight = window.innerHeight;
      
      // Set appropriate height/positioning
      chatPageRef.current.style.height = `${windowHeight}px`;
    };
    
    // Call immediately and on resize
    adjustChatPosition();
    window.addEventListener('resize', adjustChatPosition);
    
    return () => {
      window.removeEventListener('resize', adjustChatPosition);
    };
  }, []);

  const handleBackClick = () => {
    // Navigate to chapters page if in tutor mode with a chapter, otherwise to landing
    if (mode === 'tutor' && selectedChapter) {
      navigate('/chapters');
    } else {
      navigate('/');
    }
  };

  // Define the 3D models to display (only used in interview mode)
  const characterModels = [
    { 
      src: '/models/ni.glb', 
      character: '你',
      customOrbit: {
        size: 500,  
        duration: 95, 
        modelSize: 300 
      }
    },
    { src: '/models/hao.glb', character: '好' },
    { src: '/models/zhong.glb', character: '中' },
    { 
      src: '/models/wen.glb', 
      character: '文',
      customOrbit: {
        size: 550,  
        duration: 75, 
        modelSize: 180 
      }
    }
  ];

  // If no PDF is loaded, don't render the page (will redirect)
  if (!hasPdfContent) {
    return null;
  }

  const pageTitle = mode === 'tutor' 
    ? `Learning ${selectedChapter || 'Physics'}` 
    : 'Behavioral Interview Practice';
  
  const pageDescription = mode === 'tutor'
    ? 'Learn physics concepts with an AI tutor through interactive conversation'
    : 'Practice answering behavioral interview questions with our AI interviewer';

  const interviewerName = mode === 'tutor' ? 'AI Science Tutor' : 'AI Interviewer';

  // Stars for tutor mode background
  const renderStars = () => {
    if (mode === 'tutor') {
      return (
        <div className="stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
      );
    }
    return null;
  };

  return (
    <div 
      ref={chatPageRef}
      className={`chat-page ${mode === 'tutor' ? 'tutor-mode' : 'interview-mode'}`}
    >
      <SEO 
        title={pageTitle}
        description={pageDescription}
      />
      
      {/* Stars background for tutor mode */}
      {renderStars()}
      
      {/* Add floating 3D models around the chat container */}
      <FloatingModelsContainer className="chat-page-models" />
      
      {/* Main Chat Component */}
      <div className="chat-container">
        <ChatTemplate 
          onBackClick={handleBackClick} 
          interviewerName={interviewerName}
        />
      </div>
    </div>
  );
};

export default ChatPage; 